import React, { useState } from "react";
import MicOffIcon from "@mui/icons-material/MicOff";
import { useLocation } from "react-router-dom";
import {
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  FormControl,
  Card,
} from "@mui/material";
import {
  StyledButton,
  StyledInputLabel,
  StyledSelect,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledDatePicker,
  OutlinedIconFab,
  StyledTableContainer,
} from "../StyledComponents";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { durationType } from "../initialData/initialData";
import {
  getHumanReadableDiffInPastDate,
  getHumanReadableDurationInDate,
} from "../Utils/UtilsFunctions";
import Icon from "../Utils/Icons";

const AddictionHistory = ({
  addictionHistoryData,
  setAddictionHistoryData,
  dateForAppointment,
  startRecording,
  stopRecording,
  isAiEnabled,
}) => {
  const location = useLocation();
  console.log("Addiction History called", addictionHistoryData);
  const today = dayjs(dateForAppointment);
  const [addictionVars, setAddictionVars] = useState({
    addiction_description: "",
    addiction_type: "",
    onset_datetime: null,
    abatement_datetime: null,
    onset_duration: "",
    onset_duration_type: "days",
    abatement_duration: "",
    abatement_duration_type: "days",
  });
  const [addictionEditIndex, setAddictionEditIndex] = useState(null);
  const [addictionError, setAddictionError] = useState("");
  const [listeningOn, setListeningOn] = useState(false);
  console.log("addictionVars", addictionVars);
  const handleAddAddictionRow = () => {
    if (addictionVars?.addiction_description && addictionVars?.addiction_type) {
      // Store the updated state in a local variable
      console.log("New Addiction row", addictionVars);
      const newRow = {
        addiction_description: addictionVars.addiction_description,
        addiction_type: addictionVars.addiction_type,
        onset_datetime: addictionVars?.onset_datetime?.format("YYYY-MM-DD"),
        abatement_datetime:
          addictionVars?.abatement_datetime?.format("YYYY-MM-DD"),
        onset_duration: addictionVars?.onset_duration,
        onset_duration_type: addictionVars?.onset_duration_type,
        abatement_duration: addictionVars?.abatement_duration,
        abatement_duration_type: addictionVars?.abatement_duration_type,
      };

      // Update family history rows by adding the new row

      const updatedRows = [...addictionHistoryData["new"], newRow];

      setAddictionHistoryData({
        ...addictionHistoryData,
        new: updatedRows,
      });
      // Reset family history variables and clear error
      setAddictionVars({
        addiction_description: "",
        addiction_type: "",
        onset_datetime: null,
        abatement_datetime: null,
        onset_duration: "",
        onset_duration_type: "days",
        abatement_duration: "",
        abatement_duration_type: "days",
      });
      setAddictionError("");
    } else {
      // Set error message if any field is missing
      setAddictionError("Addiction Type and Addiction Description is required");
    }
  };
  const handleAddictionEditClick = (row, rowIndex) => {
    setAddictionVars({
      addiction_description: row?.addiction_description,
      addiction_type: row?.addiction_type,
      onset_datetime: row?.onset_datetime ? dayjs(row?.onset_datetime) : null,
      abatement_datetime: row?.abatement_datetime
        ? dayjs(row?.abatement_datetime)
        : null,
      onset_duration: row?.onset_duration,
      onset_duration_type: row?.onset_duration_type,
      abatement_duration: row?.abatement_duration,
      abatement_duration_type: row?.abatement_duration_type,
    });
    setAddictionEditIndex(rowIndex);
  };
  const handleAddictionUpdate = () => {
    if (addictionVars.addiction_description && addictionVars.addiction_type) {
      // Store the updated state in a local variable
      const updatedRow = {
        addiction_description: addictionVars?.addiction_description,
        addiction_type: addictionVars?.addiction_type,
        onset_datetime: addictionVars?.onset_datetime?.format("YYYY-MM-DD"),
        abatement_datetime:
          addictionVars?.abatement_datetime?.format("YYYY-MM-DD"),
        onset_duration: addictionVars?.onset_duration,
        onset_duration_type: addictionVars?.onset_duration_type,
        abatement_duration: addictionVars?.abatement_duration,
        abatement_duration_type: addictionVars?.abatement_duration_type,
      };
      const updatedAddiction = addictionHistoryData["new"].map((row, index) => {
        if (index === addictionEditIndex) {
          return updatedRow;
        } else {
          return row;
        }
      });
      setAddictionHistoryData({
        ...addictionHistoryData,
        new: updatedAddiction,
      });
      // Reset family history variables and clear error
      setAddictionVars({
        addiction_description: "",
        addiction_type: "",
        onset_datetime: null,
        abatement_datetime: null,
        onset_duration: "",
        onset_duration_type: "days",
        abatement_duration: "",
        abatement_duration_type: "days",
      });
      setAddictionEditIndex(null);
      setAddictionError("");
    } else {
      // Set error message if any field is missing
      setAddictionError("Addiction Type and Addiction Description is required");
    }
  };
  const handleAddictionDelete = (rowIndex) => {
    // Filter out the row at the specified index
    const updatedAddiction = addictionHistoryData["new"].filter(
      (row, index) => index !== rowIndex
    );
    // Update the state with the filtered array
    setAddictionHistoryData({
      ...addictionHistoryData,
      new: updatedAddiction,
    });
  };
  const handleChangeAddiction = (field, value) => {
    let updatedDetails = { ...addictionVars, [field]: value };

    // If field is onset_duration or onset_duration_type, calculate onset_datetime using helper
    if (
      (field === "onset_duration" || field === "onset_duration_type") &&
      updatedDetails.onset_duration &&
      updatedDetails.onset_duration_type
    ) {
      const calculatedDate = calculateOnsetDate(
        parseInt(updatedDetails.onset_duration),
        updatedDetails.onset_duration_type
      );

      updatedDetails.onset_datetime = calculatedDate ?? null;
    } else if (field === "onset_duration" || field === "onset_duration_type") {
      updatedDetails.onset_datetime = null;
    }

    // If field is abatement_duration or abatement_duration_type, calculate abatement_datetime using helper
    if (
      (field === "abatement_duration" || field === "abatement_duration_type") &&
      updatedDetails.abatement_duration &&
      updatedDetails.abatement_duration_type
    ) {
      const calculatedDate = calculateAbatementDate(
        updatedDetails.onset_datetime,
        parseInt(updatedDetails.abatement_duration),
        updatedDetails.abatement_duration_type
      );

      updatedDetails.abatement_datetime = calculatedDate ?? null;
    } else if (
      field === "abatement_duration" ||
      field === "abatement_duration_type"
    ) {
      updatedDetails.abatement_datetime = null;
    }

    setAddictionVars(updatedDetails);
  };
  const handleClearAddictionDescription = () => {
    setAddictionVars({
      ...addictionVars,
      addiction_description: "", // Use computed property name to set the field dynamically
    });
  };
  const calculateOnsetDate = (duration, type) => {
    if (
      duration === null ||
      duration === undefined ||
      isNaN(parseInt(duration))
    )
      return null;
    const today = dayjs(dateForAppointment); // appointment date
    const durationMap = {
      minutes: "minute",
      hours: "hour",
      days: "day",
      weeks: "week",
      months: "month",
      years: "year",
    };
    console.log("date onset", today.subtract(duration, durationMap[type]));
    return today.subtract(duration, durationMap[type]);
  };
  const calculateAbatementDate = (onset_datetime, duration, type) => {
    if (
      duration === null ||
      duration === undefined ||
      isNaN(parseInt(duration)) ||
      !onset_datetime
    )
      return null;
    const durationMap = {
      minutes: "minute",
      hours: "hour",
      days: "day",
      weeks: "week",
      months: "month",
      years: "year",
    };
    console.log(
      "date abatement",
      dayjs(onset_datetime).add(duration, durationMap[type])
    );
    return dayjs(onset_datetime).add(duration, durationMap[type]);
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ margin: "5px 0px", display: "flex", alignItems: "center" }}
      >
        <Grid item xs={11} sm={3} sx={{ margin: "5px 0px" }}>
          {/* <FormControl fullWidth> */}
          {/* <StyledInputLabel>Select Addiction Type</StyledInputLabel> */}
          <StyledTextField
            select
            label="Select Addiction Type"
            value={addictionVars.addiction_type}
            onChange={(e) =>
              handleChangeAddiction("addiction_type", e.target.value)
            }
            fullWidth
          >
            {["Tobacco Smoking", "Tobacco Chewing", "Alcohol", "Drugs"].map(
              (addiction) => (
                <MenuItem
                  key={addiction}
                  value={addiction}
                  sx={{ fontSize: "12px" }}
                >
                  {addiction}
                </MenuItem>
              )
            )}
          </StyledTextField>
          {/* </FormControl> */}
        </Grid>
        <Grid item xs={11} sm={8} sx={{ margin: "5px 0px" }}>
          <FormControl fullWidth>
            <StyledTextField
              label="Addiction Description"
              value={addictionVars.addiction_description}
              onChange={(e) =>
                handleChangeAddiction("addiction_description", e.target.value)
              }
              InputProps={{
                endAdornment: (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ marginRight: "5px" }}>
                      <Tooltip title="Clear">
                        <OutlinedIconFab
                          size="small"
                          variant="contained"
                          onClick={handleClearAddictionDescription}
                        >
                          <Icon name="clearTextField" size={32} />
                        </OutlinedIconFab>
                      </Tooltip>
                    </div>

                    <>
                      {isAiEnabled &&
                        location.pathname.includes("DoctorConsole") &&
                        (listeningOn == false ? (
                          <Tooltip title="Start Listening">
                            <OutlinedIconFab
                              // onClick={startListening}
                              onClick={() => {
                                startRecording();
                                setListeningOn(true);
                              }}
                              variant="contained"
                              color="defaultColor"
                              size="small"
                            >
                              <Icon name="micNotInUse" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Stop Listening">
                            <OutlinedIconFab
                              onClick={() => {
                                // SpeechRecognition.stopListening();
                                stopRecording("Addiction History");
                                setListeningOn(false);
                              }}
                              variant="outlined"
                              color="defaultColor"
                              size="small"
                            >
                              <Icon name="micInUse" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        ))}
                    </>
                  </div>
                ),
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={6} sm={6} md={2} style={{ display: "flex" }}>
          <Tooltip title="How long ago the addiction started">
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              Addiction Onset:
            </span>
          </Tooltip>
          <StyledTextField
            label="Duration"
            fullWidth
            type="number"
            value={addictionVars.onset_duration || ""}
            // error={!!validationErrors.duration}
            // helperText={validationErrors.duration}
            onChange={(e) =>
              handleChangeAddiction("onset_duration", e.target.value)
            }
            onWheel={(e) => e.target.blur()} // Prevent scroll change
            InputProps={{
              inputProps: {
                style: {
                  // Hide arrows in Chrome, Safari, Edge
                  MozAppearance: "textfield",
                },
              },
              sx: {
                // Hide arrows in Firefox
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
                // Hide arrows in Chrome, Safari, Edge
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={6} sm={5} md={2.5}>
          <StyledTextField
            select
            fullWidth
            label="Duration Type"
            value={addictionVars.onset_duration_type || "days"}
            onChange={(e) =>
              handleChangeAddiction("onset_duration_type", e.target.value)
            }
          >
            {durationType.map((type) => (
              <MenuItem
                key={type.value}
                value={type.value}
                sx={{ fontSize: "12px" }}
              >
                {type.display}
              </MenuItem>
            ))}
          </StyledTextField>
        </Grid>

        <Grid item xs={6} sm={6} md={2} style={{ display: "flex" }}>
          <Tooltip title="How long the person has been addicted">
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              Addiction Duration:
            </span>
          </Tooltip>
          <StyledTextField
            label="Duration"
            fullWidth
            type="number"
            value={addictionVars.abatement_duration || ""}
            // error={!!validationErrors.duration}
            // helperText={validationErrors.duration}
            onChange={(e) =>
              handleChangeAddiction("abatement_duration", e.target.value)
            }
            onWheel={(e) => e.target.blur()} // Prevent scroll change
            InputProps={{
              inputProps: {
                style: {
                  // Hide arrows in Chrome, Safari, Edge
                  MozAppearance: "textfield",
                },
              },
              sx: {
                // Hide arrows in Firefox
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
                // Hide arrows in Chrome, Safari, Edge
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={6} sm={5} md={2.5}>
          <StyledTextField
            select
            fullWidth
            label="Duration Type"
            value={addictionVars.abatement_duration_type || "days"}
            onChange={(e) =>
              handleChangeAddiction("abatement_duration_type", e.target.value)
            }
          >
            {durationType.map((type) => (
              <MenuItem
                key={type.value}
                value={type.value}
                sx={{ fontSize: "12px" }}
              >
                {type.display}
              </MenuItem>
            ))}
          </StyledTextField>
        </Grid>

        <Grid item xs={11} md={3} sx={{ margin: "5px 0px", display: "none" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <StyledDatePicker
                label="Start Date"
                onChange={(date) =>
                  handleChangeAddiction("onset_datetime", date)
                }
                slotProps={{
                  field: { clearable: true },
                }}
                maxDate={today}
                value={
                  addictionVars?.onset_datetime
                    ? dayjs(addictionVars?.onset_datetime)
                    : null
                }
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={11} md={3} sx={{ margin: "5px 0px", display: "none" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <StyledDatePicker
                fullWidth
                label="End Date"
                onChange={(date) =>
                  handleChangeAddiction("abatement_datetime", date)
                }
                slotProps={{
                  field: { clearable: true },
                }}
                maxDate={today}
                minDate={dayjs(addictionVars.onset_datetime)}
                value={
                  addictionVars?.abatement_datetime
                    ? dayjs(addictionVars?.abatement_datetime)
                    : null
                }
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid
          item
          xs={11}
          sm={2}
          sx={{
            margin: "5px 0px",
            display: "flex",
            justifyContent: "flex-end",
            padding: "0px",
          }}
        >
          <StyledButton
            bgColor="#0E3F66"
            variant="contained"
            fullWidth
            onClick={
              addictionEditIndex !== null
                ? handleAddictionUpdate
                : handleAddAddictionRow
            }
            color="defaultColor"
          >
            {addictionEditIndex !== null ? "Update" : "Add"}
          </StyledButton>
        </Grid>
        <Grid item xs={11} md={11} sm={11}>
          {addictionError && (
            <Typography style={{ color: "red" }}>{addictionError}</Typography>
          )}
        </Grid>

        <Grid item xs={11} sm={11}>
          {addictionHistoryData["prev"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>
                      Addiction Type
                    </StyledTableCellHistory>

                    <StyledTableCellHistory>
                      Addiction Description
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Addiction Onset
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Addiction Duration
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {addictionHistoryData["prev"].map((row, rowIndex) => {
                    // console.log("Addiction Row", row); // Logging the row object

                    return (
                      <StyledTableRowHistory key={rowIndex}>
                        <StyledTableCellHistory>
                          {row?.addiction_type}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.addiction_description}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.onset_datetime
                            ? `${getHumanReadableDiffInPastDate(
                              row?.onset_datetime
                            )} ago`
                            : "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.abatement_datetime
                            ? getHumanReadableDurationInDate(
                              row?.onset_datetime,
                              row?.abatement_datetime
                            )
                            : "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>-</StyledTableCellHistory>
                      </StyledTableRowHistory>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
        <Grid item xs={11} sm={11}>
          {addictionHistoryData["new"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>
                      Addiction Type
                    </StyledTableCellHistory>

                    <StyledTableCellHistory>
                      Addiction Description
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Addiction Onset
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>
                      Addiction Duration
                    </StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {addictionHistoryData["new"].map((row, rowIndex) => {
                    console.log("Addiction Row", row); // Logging the row object

                    return (
                      <StyledTableRowHistory key={rowIndex}>
                        <StyledTableCellHistory>
                          {row?.addiction_type ?? "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.addiction_description ?? "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.onset_duration
                            ? `${row?.onset_duration} ${row?.onset_duration_type} ago`
                            : "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {row?.abatement_duration
                            ? `${row?.abatement_duration} ${row?.abatement_duration_type}`
                            : "-"}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {addictionHistoryData["prev"].includes(row) ? (
                            "-"
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <Tooltip title="Edit">
                                <OutlinedIconFab
                                  onClick={() =>
                                    handleAddictionEditClick(row, rowIndex)
                                  }
                                  sx={{ marginRight: 1 }}
                                >
                                  <Icon name="editInTable" size={32} />
                                </OutlinedIconFab>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <OutlinedIconFab
                                  onClick={() =>
                                    handleAddictionDelete(rowIndex)
                                  }
                                >
                                  <Icon name="deleteInTable" size={32} />
                                </OutlinedIconFab>
                              </Tooltip>
                            </div>
                          )}
                        </StyledTableCellHistory>
                      </StyledTableRowHistory>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default AddictionHistory;
